/**
 * Polyfill for Dynamic Viewport Units (dvh, dvw, dvi, dvb)
 * Provides fallback support for browsers that don't support these CSS units
 */

class ViewportPolyfill {
  constructor() {
    this.isSupported = this.checkSupport();
    this.isInitialized = false;

    if (!this.isSupported) {
      this.init();
    }
  }

  /**
   * Check if browser supports dynamic viewport units
   */
  checkSupport() {
    if (typeof window === "undefined") return true;

    try {
      // Create a test element to check dvh support
      const testEl = document.createElement("div");
      testEl.style.height = "100dvh";
      document.body.appendChild(testEl);

      const computedStyle = window.getComputedStyle(testEl);
      const height = computedStyle.height;

      document.body.removeChild(testEl);

      // If dvh is supported, height should not be 'auto' or empty
      return height !== "auto" && height !== "" && height !== "0px";
    } catch (e) {
      return false;
    }
  }

  /**
   * Initialize the polyfill
   */
  init() {
    if (this.isInitialized) return;

    this.updateViewportValues();
    this.bindEvents();
    this.patchCSS();
    this.isInitialized = true;

    console.log("🔧 Viewport polyfill initialized: dvh fallback active");
  }

  /**
   * Calculate and update viewport values
   */
  updateViewportValues() {
    if (typeof window === "undefined") return;

    // Get the actual viewport dimensions
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // For mobile browsers, we need to handle the address bar
    const dvh = this.getDynamicViewportHeight();
    const dvw = window.innerWidth;

    // Set CSS custom properties as fallback
    document.documentElement.style.setProperty("--dvh", `${dvh}px`);
    document.documentElement.style.setProperty("--dvw", `${dvw}px`);
    document.documentElement.style.setProperty("--dvi", `${Math.min(dvw, dvh)}px`);
    document.documentElement.style.setProperty("--dvb", `${Math.max(dvw, dvh)}px`);

    // Also set the 1% values for calc() operations
    document.documentElement.style.setProperty("--dvh-1", `${dvh / 100}px`);
    document.documentElement.style.setProperty("--dvw-1", `${dvw / 100}px`);
  }

  /**
   * Get dynamic viewport height (handles mobile address bar and keyboard)
   */
  getDynamicViewportHeight() {
    if (typeof window === "undefined") return 0;

    // Use visualViewport API if available (better for mobile)
    if (window.visualViewport) {
      // Kiểm tra xem keyboard có đang hiện không
      const isKeyboardVisible = window.visualViewport.height < window.innerHeight * 0.75;

      // Nếu keyboard hiện, sử dụng visualViewport height
      // Nếu không, sử dụng innerHeight để tránh thay đổi khi address bar ẩn/hiện
      return isKeyboardVisible ? window.visualViewport.height : window.innerHeight;
    }

    // Fallback to window.innerHeight
    return window.innerHeight;
  }

  /**
   * Bind event listeners for viewport changes
   */
  bindEvents() {
    if (typeof window === "undefined") return;

    // Debounce function to avoid too many updates
    let timeoutId;
    const debouncedUpdate = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        this.updateViewportValues();
        this.updatePatchedStyles();
      }, 100);
    };

    // Standard resize event
    window.addEventListener("resize", debouncedUpdate);

    // Orientation change (mobile)
    window.addEventListener("orientationchange", () => {
      // Small delay for orientation change to complete
      setTimeout(() => {
        this.updateViewportValues();
        this.updatePatchedStyles();
      }, 200);
    });

    // Visual viewport changes (mobile address bar, virtual keyboard)
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", debouncedUpdate);
    }

    // Scroll events for mobile address bar detection
    let scrollTimeout;
    window.addEventListener(
      "scroll",
      () => {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          this.updateViewportValues();
          this.updatePatchedStyles();
        }, 150);
      },
      { passive: true }
    );
  }

  /**
   * Patch CSS to replace dvh units with custom properties
   */
  patchCSS() {
    if (typeof window === "undefined") return;

    // Add polyfill CSS
    const style = document.createElement("style");
    style.textContent = `
      /* Viewport Units Polyfill */
      .h-dvh { height: var(--dvh) !important; }
      .min-h-dvh { min-height: var(--dvh) !important; }
      .max-h-dvh { max-height: var(--dvh) !important; }
      .w-dvw { width: var(--dvw) !important; }
      .min-w-dvw { min-width: var(--dvw) !important; }
      .max-w-dvw { max-width: var(--dvw) !important; }
      
      /* Support for inline styles - will be handled by JS */
    `;
    document.head.appendChild(style);

    // Patch inline styles that use dvh
    this.patchInlineStyles();
  }

  /**
   * Find and patch inline styles using dvh units
   */
  patchInlineStyles() {
    if (typeof window === "undefined") return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "attributes" && mutation.attributeName === "style") {
          this.patchElementStyle(mutation.target);
        }

        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            this.patchElementAndChildren(node);
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style"],
    });

    // Patch existing elements
    this.patchElementAndChildren(document.body);
  }

  /**
   * Patch a single element and its children
   */
  patchElementAndChildren(element) {
    this.patchElementStyle(element);
    element.querySelectorAll('*[style*="dvh"], *[style*="dvw"]').forEach((el) => {
      this.patchElementStyle(el);
    });
  }

  /**
   * Patch inline style of a single element
   */
  patchElementStyle(element) {
    if (!element.style) return;

    const style = element.style.cssText;
    if (style.includes("dvh") || style.includes("dvw")) {
      let newStyle = style;

      // Replace dvh units
      newStyle = newStyle.replace(/(\d*\.?\d+)dvh/g, (match, value) => {
        const dvhValue = parseFloat(value);
        const pixels = (dvhValue / 100) * this.getDynamicViewportHeight();
        return `${pixels}px`;
      });

      // Replace dvw units
      newStyle = newStyle.replace(/(\d*\.?\d+)dvw/g, (match, value) => {
        const dvwValue = parseFloat(value);
        const pixels = (dvwValue / 100) * window.innerWidth;
        return `${pixels}px`;
      });

      if (newStyle !== style) {
        element.style.cssText = newStyle;
        // Store original for re-calculation on resize
        element.dataset.originalStyle = style;
      }
    }
  }

  /**
   * Update all patched inline styles
   */
  updatePatchedStyles() {
    document.querySelectorAll("[data-original-style]").forEach((element) => {
      const originalStyle = element.dataset.originalStyle;
      element.style.cssText = originalStyle;
      this.patchElementStyle(element);
    });
  }

  /**
   * Get current viewport values
   */
  getViewportValues() {
    return {
      dvh: this.getDynamicViewportHeight(),
      dvw: window.innerWidth,
      isPolyfilled: !this.isSupported,
    };
  }
}

// Create and export singleton instance
let viewportPolyfill;

export function initViewportPolyfill() {
  if (!viewportPolyfill) {
    viewportPolyfill = new ViewportPolyfill();
  }
  return viewportPolyfill;
}

export function getViewportValues() {
  if (!viewportPolyfill) {
    initViewportPolyfill();
  }
  return viewportPolyfill.getViewportValues();
}

// Debug utility to check polyfill status
export function debugViewportPolyfill() {
  if (!viewportPolyfill) {
    console.log("❌ Viewport polyfill not initialized");
    return;
  }

  const values = viewportPolyfill.getViewportValues();
  console.log("🔍 Viewport Polyfill Status:", {
    isPolyfilled: values.isPolyfilled,
    dvh: values.dvh,
    dvw: values.dvw,
    windowInnerHeight: window.innerHeight,
    visualViewport: window.visualViewport?.height || "not available",
  });
}

// Auto-initialize if in browser environment
if (typeof window !== "undefined") {
  // Wait for DOM to be ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initViewportPolyfill);
  } else {
    initViewportPolyfill();
  }
}

export default ViewportPolyfill;
